import { useMemo } from 'react';
import { SubscriptionRecord } from './useSubscriptionData';
import { STRIPE_PERCENT_FEE, STRIPE_FIXED_FEE } from '@/utils/fees/constants';

interface SavingsMetrics {
  totalSaved: number;
  remainingEligible: number;
  migratedCount: number;
  eligibleCount: number;
}

const calculateStripeFee = (amountInCents: number): number => {
  // Stripe fee = (amount * 2.9%) + $0.30
  const percentageFee = Math.round(amountInCents * STRIPE_PERCENT_FEE);
  const fixedFeeCents = Math.round(STRIPE_FIXED_FEE * 100);
  return percentageFee + fixedFeeCents;
};

export const useSubscriptionSavings = (subscriptions: SubscriptionRecord[]): SavingsMetrics => {
  return useMemo(() => {
    let totalSaved = 0;
    let remainingEligible = 0;
    let migratedCount = 0;
    let eligibleCount = 0;

    subscriptions.forEach(sub => {
      if (sub.status !== 'active') {
        return;
      }

      const amount = sub.amount || 0;
      
      const stripeFee = calculateStripeFee(amount);

      // Annualize the fee based on billing frequency
      let annualMultiplier = 1;
      if (sub.billing_interval === 'month') {
        annualMultiplier = 12;
      } else if (sub.billing_interval === 'year') {
        annualMultiplier = 1;
      } else if (sub.billing_interval === 'week') {
        annualMultiplier = 52;
      } else if (sub.billing_interval === 'day') {
        annualMultiplier = 365;
      }

      const annualizedFee = stripeFee * annualMultiplier;

      const isMigrated = sub.has_pending_upgrade && (
        sub.upgrade_status === 'upgraded' || 
        sub.upgrade_status === 'payment_method_updated'
      );
      
      if (isMigrated) {
        totalSaved += annualizedFee;
        migratedCount++;
      } else {
        remainingEligible += annualizedFee;
        eligibleCount++;
      }
    });

    return {
      totalSaved: totalSaved / 100,
      remainingEligible: remainingEligible / 100,
      migratedCount,
      eligibleCount,
    };
  }, [subscriptions]);
};

